import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Button,
  Collapse,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import { ExpandMore, ExpandLess, Analytics as AnalyticsIcon } from '@mui/icons-material';
import api from '../../services/authService';

const MeetingAnalyticsCard = ({ meetingId, isExpanded = false }) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (expanded && !analytics) {
      fetchAnalytics();
    }
  }, [expanded]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/analytics/meetings/${meetingId}/summary`);
      setAnalytics(response.data.analytics);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  const handleCalculate = async () => {
    try {
      setLoading(true);
      await api.post(`/analytics/meetings/${meetingId}/calculate`, {});
      await fetchAnalytics();
    } catch (err) {
      console.error('Error calculating analytics:', err);
      setError('Failed to calculate analytics');
      setLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <AnalyticsIcon color="primary" />
            <Typography variant="h6">
              Meeting Analytics
            </Typography>
          </Box>
          <IconButton onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            {loading && (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Typography color="error" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}

            {!loading && !analytics && !error && (
              <Box textAlign="center" p={2}>
                <Typography color="textSecondary" gutterBottom>
                  Analytics not yet calculated for this meeting
                </Typography>
                <Button
                  variant="contained"
                  onClick={handleCalculate}
                  sx={{ mt: 1 }}
                >
                  Calculate Analytics
                </Button>
              </Box>
            )}

            {!loading && analytics && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Duration
                    </Typography>
                    <Typography variant="h6">
                      {formatDuration(analytics.totalDurationSeconds)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Participants
                    </Typography>
                    <Typography variant="h6">
                      {analytics.participantCount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Engagement Score
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {Math.round(analytics.engagementScore)}
                      </Typography>
                      <Chip
                        label={
                          analytics.engagementScore >= 80
                            ? 'Excellent'
                            : analytics.engagementScore >= 60
                            ? 'Good'
                            : 'Low'
                        }
                        color={getScoreColor(analytics.engagementScore)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="textSecondary">
                      Quality Score
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6">
                        {Math.round(analytics.qualityScore)}
                      </Typography>
                      <Chip
                        label={
                          analytics.qualityScore >= 80
                            ? 'Excellent'
                            : analytics.qualityScore >= 60
                            ? 'Good'
                            : 'Poor'
                        }
                        color={getScoreColor(analytics.qualityScore)}
                        size="small"
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                  Summary
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Chat Messages
                    </Typography>
                    <Typography variant="body1">
                      {analytics.totalChatMessages}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Hand Raises
                    </Typography>
                    <Typography variant="body1">
                      {analytics.handRaiseCount}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Screen Share
                    </Typography>
                    <Typography variant="body1">
                      {formatDuration(analytics.screenShareSeconds)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="body2" color="textSecondary">
                      Avg RTT
                    </Typography>
                    <Typography variant="body1">
                      {analytics.avgRttMs?.toFixed(1) || 0} ms
                    </Typography>
                  </Grid>
                </Grid>

                {analytics.participantAnalytics && analytics.participantAnalytics.length > 0 && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Participant Breakdown
                    </Typography>
                    <TableContainer component={Paper} elevation={0} sx={{ maxHeight: 300 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Speaking %</TableCell>
                            <TableCell>Messages</TableCell>
                            <TableCell>Avg RTT</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {analytics.participantAnalytics.map((participant) => (
                            <TableRow key={participant.id}>
                              <TableCell>{participant.displayName}</TableCell>
                              <TableCell>
                                {participant.speakingPercentage?.toFixed(1) || 0}%
                              </TableCell>
                              <TableCell>{participant.chatMessagesCount}</TableCell>
                              <TableCell>
                                {participant.avgRttMs?.toFixed(1) || 0} ms
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default MeetingAnalyticsCard;
